import React, { useEffect, useState } from 'react';
import { Toast as ToastType } from './ToastContext';
import { useToast } from './ToastContext';

// Icons for different toast types
const Icons = {
  success: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const toastStyles = {
  success: {
    container:
      'bg-green-50 border-y border-r border-green-200',
    icon: 'text-green-600',
    text: 'text-gray-900',
    progress: 'bg-green-600',
  },
  error: {
    container:
      'bg-red-50 border-y border-r border-red-200',
    icon: 'text-red-600',
    text: 'text-gray-900',
    progress: 'bg-red-600',
  },
  warning: {
    container:
      'bg-amber-50 border-y border-r border-amber-200',
    icon: 'text-amber-600',
    text: 'text-gray-900',
    progress: 'bg-amber-600',
  },
  info: {
    container:
      'bg-primary-50 border-l-4 border-l-primary-500 border-y border-r border-primary-200',
    icon: 'text-primary-600',
    text: 'text-gray-900',
    progress: 'bg-primary-600',
  },
};


interface ToastItemProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const styles = toastStyles[toast.type];
  const duration = toast.duration || 4000;
  const autoClose = toast.autoClose !== false;

  useEffect(() => {
    if (!autoClose) return;
    
    // Progress bar animation
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 50);

    // Exit animation before removal
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 300);

    return () => {
      clearInterval(interval);
      clearTimeout(exitTimer);
    };
  }, [duration, autoClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div
      className={`
        relative flex items-start gap-3 p-3 sm:p-4 rounded-lg shadow-lg
        w-full sm:min-w-[320px] sm:max-w-[420px] overflow-hidden
        transform transition-all duration-300 ease-out
        ${styles.container}
        ${isExiting 
          ? 'opacity-0 translate-y-[-100%] sm:translate-y-0 sm:translate-x-full scale-95' 
          : 'opacity-100 translate-y-0 sm:translate-x-0 scale-100'
        }
      `}
      role="alert"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 p-1.5 rounded-full ${styles.icon}`}>
        {Icons[toast.type]}
      </div>

      {/* Message */}
      <div className={`flex-1 pt-0.5 text-sm font-medium ${styles.text}`}>
        {toast.message}
      </div>

      {/* Close button */}
      <button
        onClick={handleClose}
        className={`
          flex-shrink-0 p-1 rounded-full transition-colors
          hover:bg-black/10
          ${styles.text}
        `}
        aria-label="Close"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Progress bar - only show when autoClose is enabled */}
      {autoClose && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full transition-all duration-100 ease-linear ${styles.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 z-[9999] flex flex-col gap-2 sm:gap-3"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
};

export default ToastContainer;
